import React, { useState, useEffect } from 'react';
import fnolStateService from '../../services/fnolStateService';
import './ClaimsSetUp.css';
import { fnolAPI, documentsAPI } from '../../api';
import ClaimConfirmationModal from '../../components/ClaimConfirmationModal';
import { normalizeApiTimeToInput } from '../../utils/time';

const ClaimsSetUpPage = ({ selected, isCompleted, setIsCompleted, onPrevious, validityData, goToDataHandling, goToValidation, goToTriaging, hasClaimId, onClaimGenerated, allPriorStepsCompleted }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [localValidityData, setLocalValidityData] = useState(null);
    const [loadingValidity, setLoadingValidity] = useState(false);
    const [assignmentData, setAssignmentData] = useState(null);
    const [loadingAssignment, setLoadingAssignment] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [loadingClaim, setLoadingClaim] = useState(false);
    const [claimError, setClaimError] = useState(null);

    const ASSIGNMENT_API_BASE_URL = 'https://fnolbackend.alphatorana.com/api/assignment';
    const CLAIMS_API_BASE_URL = 'https://fnolbackend.alphatorana.com/api';

    const getProgressBarColor = (accuracy) => {
        if (accuracy === 100) return '#8CE27D'; // Green
        if (accuracy >= 50) return '#FFD996'; // Yellow
        return '#FFA6A6'; // Red
    };

    const formatSubLimitDisplay = (value) => {
        if (!value) return '';
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'string') return value;
        if (typeof value !== 'object') return String(value);

        // For object shapes, show selected keys (same truthy rules as FNOLForm)
        const selectedKeys = Object.keys(value).filter((key) => {
            const v = value[key];
            return v !== '' && v !== '0' && v !== false && v !== null && v !== undefined;
        });
        return selectedKeys.join(', ');
    };

    // Use local validity data if available, otherwise fall back to prop
    const currentValidityData = localValidityData || validityData;
    const validationProgress = currentValidityData?.overall_score !== undefined ? currentValidityData.overall_score : 0;
    const validationSummary = currentValidityData?.ai_summary || "Validation summary not available.";
    const validationStatus = validationProgress >= 50 ? 'Valid' : 'Invalid';

    const handleComplete = async () => {
        if (!selected?.id) return;
        // Prevent status changes if claim ID exists
        if (hasClaimId) {
            console.log('⚠️ Cannot change completion status - claim already exists');
            return;
        }
        // Mark as completed (always true when confirming)
        setIsCompleted(true);

        // Update API state
        try {
            await fnolStateService.updateState(selected.id, {
                ClaimsSetup: 'completed'
            });
            console.log('✅ Claims Setup completion state updated in API');
        } catch (error) {
            console.error('Failed to update Claims Setup state in API:', error);
        }

    };

    // Executes after user confirms in the modal
    const executeCompleteAndGenerateClaim = async () => {
        setShowConfirmModal(false);
        if (!selected?.id || hasClaimId) return;

        // Step 1: Mark this step as completed in the progress bar
        await handleComplete();

        // Step 2: Generate claim ID
        setLoadingClaim(true);
        setClaimError(null);
        try {
            const response = await fetch(`${CLAIMS_API_BASE_URL}/claims/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'ngrok-skip-browser-warning': 'true'
                },
                body: JSON.stringify({ fnol_id: selected.id })
            });

            if (response.ok) {
                const data = await response.json();
                if (onClaimGenerated) {
                    onClaimGenerated(data.claim_id);
                }
            } else {
                const errorData = await response.json();
                setClaimError(errorData.detail || 'Failed to create claim');
                console.error('Error creating claim:', errorData);
            }
        } catch (err) {
            setClaimError('Failed to create claim. Please try again.');
            console.error('Error creating claim:', err);
        } finally {
            setLoadingClaim(false);
        }
    };

    // Handles the unified button click — opens confirmation modal
    const handleCompleteAndGenerateClaim = () => {
        if (!selected?.id || hasClaimId || !allPriorStepsCompleted) return;
        setShowConfirmModal(true);
    };

    /* ---------- Helpers ---------- */
    // const Req = () => <span className="text-danger ms-1">*</span>;
    const Req = () => <span className="text-danger ms-1"></span>;

    const [isEditMode, setIsEditMode] = useState(false);
    const [originalForm, setOriginalForm] = useState(null); // To store state for cancel

    const [form, setForm] = useState({
        lossName: '',
        cause: '',
        claimant: '',
        dateOfLoss: '',
        timeOfLoss: '',
        reportedDate: '',
        currency: '',
        amount: '',
        location: '',
        subLimitType: '',
        policyNumber: '',
        insuredName: '',
        policyStatus: '',
        inceptionDate: '',
        expirationDate: '',
        lineOfBusiness: '',
        broker: '',
        region: '',
        adjuster: '',
        handler: ''
    });


    useEffect(() => {
        const fetchFNOLData = async () => {
            console.log('ClaimsSetUp - selected:', selected);
            if (selected?.id) {
                setLoading(true);
                setError(null);
                try {
                    const response = await fnolAPI.getFNOL(selected.id);
                    console.log('FNOL API Response:', response);
                    if (response.success && response.data) {
                        const data = response.data;
                        console.log('FNOL Data:', data);

                        // Format report_date to YYYY-MM-DD
                        const formatDate = (dateString) => {
                            if (!dateString) return '';
                            try {
                                const date = new Date(dateString);
                                return date.toISOString().split('T')[0];
                            } catch {
                                return dateString;
                            }
                        };

                        // Build initial form with FNOL data
                        let initialForm = {
                            lossName: data.loss_name || '',
                            cause: data.cause_of_loss || '',
                            claimant: data.claimant || '',
                            dateOfLoss: data.date_of_loss || '',
                            timeOfLoss: normalizeApiTimeToInput(data.time_of_loss),
                            reportedDate: formatDate(data.report_date),
                            currency: data.estimated_loss_currency || '',
                            amount: data.estimated_loss_amount?.toString() || '',
                            location: data.loss_location || '',
                            subLimitType: data.sublimit ?? data.sub_limit_type ?? data.sub_limit ?? '',
                            policyNumber: data.policy_number || '',
                            insuredName: '',
                            policyStatus: '',
                            inceptionDate: '',
                            expirationDate: '',
                            lineOfBusiness: '',
                            broker: '',
                            region: '',
                            adjuster: '',
                            handler: ''
                        };

                        // Fetch policy details if policy_number exists
                        if (data.policy_number) {
                            try {
                                const policyResponse = await fnolAPI.getPolicyDetails(data.policy_number);
                                console.log('Policy API Response:', policyResponse);
                                if (policyResponse.success && policyResponse.data) {
                                    const policyData = policyResponse.data;
                                    console.log('Policy Data:', policyData);

                                    // Merge policy details into form
                                    initialForm = {
                                        ...initialForm,
                                        policyNumber: policyData.policy_number || initialForm.policyNumber,
                                        insuredName: policyData.insured_name || initialForm.insuredName,
                                        policyStatus: policyData.policy_status || initialForm.policyStatus,
                                        inceptionDate: policyData.inception_date || initialForm.inceptionDate,
                                        expirationDate: policyData.expiration_date || initialForm.expirationDate,
                                        lineOfBusiness: policyData.line_of_business || initialForm.lineOfBusiness,
                                        broker: policyData.broker_name || initialForm.broker
                                    };
                                }
                            } catch (policyErr) {
                                console.error('Error fetching policy details:', policyErr);
                                // Continue with FNOL data even if policy fetch fails
                            }
                        }

                        console.log('Setting form with merged data:', initialForm);
                        setForm(initialForm);
                        setOriginalForm(initialForm);

                        // Fetch assignment data
                        setLoadingAssignment(true);
                        try {
                            const assignmentResponse = await fetch(`${ASSIGNMENT_API_BASE_URL}/${selected.id}`, {
                                headers: {
                                    'ngrok-skip-browser-warning': 'true'
                                }
                            });

                            if (assignmentResponse.ok) {
                                const assignmentJson = await assignmentResponse.json();
                                console.log('Assignment API Response:', assignmentJson);
                                setAssignmentData(assignmentJson);

                                // Update form with assignment data
                                setForm(prev => ({
                                    ...prev,
                                    region: assignmentJson.adjuster_region || prev.region,
                                    adjuster: assignmentJson.claim_adjuster || prev.adjuster,
                                    handler: assignmentJson.claim_handler || prev.handler
                                }));
                                setOriginalForm(prev => ({
                                    ...prev,
                                    region: assignmentJson.adjuster_region || prev.region,
                                    adjuster: assignmentJson.claim_adjuster || prev.adjuster,
                                    handler: assignmentJson.claim_handler || prev.handler
                                }));
                            } else if (assignmentResponse.status === 404) {
                                console.log('No assignment found for this FNOL');
                                setAssignmentData(null);
                            } else {
                                console.error('Failed to fetch assignment data');
                            }
                        } catch (assignmentErr) {
                            console.error('Error fetching assignment data:', assignmentErr);
                        } finally {
                            setLoadingAssignment(false);
                        }

                        // Fetch validity data
                        setLoadingValidity(true);
                        try {
                            const validityResponse = await fnolAPI.getValidityCheck(selected.id);
                            console.log('Validity API Response:', validityResponse);
                            if (validityResponse.success && validityResponse.data) {
                                setLocalValidityData(validityResponse.data);
                                console.log('Validity data set:', validityResponse.data);
                            } else if (validityResponse.overall_score !== undefined) {
                                // Fallback if response is directly the data
                                setLocalValidityData(validityResponse);
                                console.log('Validity data set (direct):', validityResponse);
                            }
                        } catch (validityErr) {
                            console.error('Error fetching validity data:', validityErr);
                            // Continue without validity data
                        } finally {
                            setLoadingValidity(false);
                        }

                        // Fetch documents data
                        setLoadingDocuments(true);
                        try {
                            const documentsResponse = await documentsAPI.getDocuments(selected.id);
                            console.log('Documents API Response:', documentsResponse);
                            if (documentsResponse.attachments && documentsResponse.attachments.length > 0) {
                                const transformedDocuments = documentsResponse.attachments.map((attachment, index) => ({
                                    id: attachment.metadata.id,
                                    name: attachment.metadata.document_name,
                                    preview_url: attachment.preview_url.replace('http://', 'https://'),
                                    download_url: attachment.download_url.replace('http://', 'https://'),
                                    uploaded_at: attachment.metadata.created_at,
                                    category: attachment.metadata.document_category,
                                    description: attachment.metadata.document_description,
                                    ai_suggested_name: attachment.metadata.ai_suggested_document_name,
                                    document_id: attachment.metadata.document_id,
                                    uniqueKey: `${attachment.metadata.id}-${index}`
                                }));
                                setDocuments(transformedDocuments);
                                console.log('Documents loaded:', transformedDocuments);
                            } else {
                                setDocuments([]);
                            }
                        } catch (documentsErr) {
                            console.error('Error fetching documents:', documentsErr);
                            setDocuments([]);
                        } finally {
                            setLoadingDocuments(false);
                        }
                    }
                } catch (err) {
                    console.error('Error fetching FNOL data:', err);
                    setError('Failed to load FNOL details');
                } finally {
                    setLoading(false);
                }
            } else if (selected?.data) {
                // Fallback to using selected.data if available
                const data = selected.data;
                const initialForm = {
                    lossName: data.loss_name || '',
                    cause: data.cause_of_loss || '',
                    claimant: data.claimant || '',
                    dateOfLoss: data.date_of_loss || '',
                    timeOfLoss: normalizeApiTimeToInput(data.time_of_loss),
                    reportedDate: data.reported_date || '',
                    currency: data.estimated_loss_currency || '',
                    amount: data.estimated_loss_amount?.toString() || '',
                    location: data.loss_location || '',
                    subLimitType: data.sublimit ?? data.sub_limit_type ?? data.sub_limit ?? '',
                    policyNumber: data.policy_number || '',
                    insuredName: data.insured_name || '',
                    policyStatus: data.policy_status || '',
                    inceptionDate: data.inception_date || '',
                    expirationDate: data.expiration_date || '',
                    lineOfBusiness: data.line_of_business || '',
                    broker: data.broker_name || '',
                    region: data.region || '',
                    adjuster: data.claim_adjuster || '',
                    handler: data.claim_handler || ''
                };
                setForm(initialForm);
                setOriginalForm(initialForm);
            } else {
                setOriginalForm(form);
            }
        };

        fetchFNOLData();
    }, [selected]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleEdit = () => {
        if (hasClaimId) return; // Prevent editing if claim exists
        setOriginalForm(form);
        setIsEditMode(true);
    };

    const handleCancel = () => {
        if (originalForm) {
            setForm(originalForm);
        }
        setIsEditMode(false);
    };

    const handleSave = () => {
        // Here you would typically save to API
        // For now just exit edit mode
        setIsEditMode(false);
    };

    /* ---------- Comments Logic ---------- */
    const [comment, setComment] = useState('');
    const commentTextareaRef = React.useRef(null);

    const applyFormatting = (prefix, suffix = '') => {
        const textarea = commentTextareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = comment.substring(start, end);
        const beforeText = comment.substring(0, start);
        const afterText = comment.substring(end);

        const newText = beforeText + prefix + selectedText + suffix + afterText;
        setComment(newText);

        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + prefix.length + selectedText.length + suffix.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const handleBold = () => applyFormatting('**', '**');
    const handleItalic = () => applyFormatting('*', '*');
    const handleUnderline = () => applyFormatting('<u>', '</u>');
    const handleStrikethrough = () => applyFormatting('~~', '~~');
    const handleBulletList = () => {
        const textarea = commentTextareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const beforeText = comment.substring(0, start);
        const afterText = comment.substring(start);
        const newText = beforeText + '\n• ';
        setComment(newText + afterText);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newText.length, newText.length);
        }, 0);
    };
    const handleNumberedList = () => {
        const textarea = commentTextareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const beforeText = comment.substring(0, start);
        const afterText = comment.substring(start);
        const newText = beforeText + '\n1. ';
        setComment(newText + afterText);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newText.length, newText.length);
        }, 0);
    };

    return (
        <div
            className="h-100 overflow-auto auto-hide-scrollbar"
        >

            <div className="card border-0 bg-transparent">
                <div className="card-body p-0">
                    {/* Loss Details */}
                    <section className="card claims-card mb-2" style={{ borderRadius: '10px' }}>
                        <div className="card-body">
                            <h5
                                className="card-title mb-3 fw-bold"
                            >
                                <span onClick={goToDataHandling} style={{ cursor: 'pointer', color: '#006BB3', textDecoration: 'underline' }}>
                                    Loss Details
                                </span>
                            </h5>
                            <div className="row g-3 ps-2">
                                {/* Row 1 */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Loss Name <Req /></label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        placeholder="Enter Loss Name"
                                        value={form.lossName}
                                        onChange={(e) => handleChange('lossName', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Cause of Loss <Req /></label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        value={form.cause}
                                        onChange={(e) => handleChange('cause', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Claimant</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        placeholder="Enter Claimant Name"
                                        value={form.claimant}
                                        onChange={(e) => handleChange('claimant', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>

                                {/* Row 2 */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Date of Loss <Req /></label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm claims-input"
                                        value={form.dateOfLoss}
                                        onChange={(e) => handleChange('dateOfLoss', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Time of Loss <Req /></label>
                                    <input
                                        type="time"
                                        className="form-control form-control-sm claims-input"
                                        value={form.timeOfLoss}
                                        onChange={(e) => handleChange('timeOfLoss', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Reported Date <Req /></label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm claims-input"
                                        value={form.reportedDate}
                                        onChange={(e) => handleChange('reportedDate', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>

                                {/* Row 3 */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Loss Currency <Req /></label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        value={form.currency}
                                        onChange={(e) => handleChange('currency', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Estimated Loss Amount <Req /></label>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm claims-input"
                                        value={form.amount}
                                        onChange={(e) => handleChange('amount', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Loss Location <Req /></label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        value={form.location}
                                        onChange={(e) => handleChange('location', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label claims-label">Sub-Limit Type</label>
                                    <div
                                        className="form-control form-control-sm claims-input d-flex flex-wrap align-items-center gap-1"
                                        style={{ backgroundColor: '#e9ecef', minHeight: '31px', padding: '2px 8px' }}
                                    >
                                        {formatSubLimitDisplay(form.subLimitType)
                                            ? formatSubLimitDisplay(form.subLimitType).split(',').map((type, idx) => type.trim() ? (
                                                <span
                                                    key={idx}
                                                    className="badge text-dark"
                                                    style={{ backgroundColor: '#c9d0d9', fontWeight: 'normal', padding: '3px 6px', fontSize: '12px', borderRadius: '4px' }}
                                                >
                                                    {type.trim()}
                                                </span>
                                            ) : null)
                                            : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Policy Details */}
                    <section className="card claims-card mb-2" style={{ borderRadius: '10px' }}>
                        <div className="card-body">
                            <h5
                                className="card-title mb-3 fw-bold"
                            >
                                <span onClick={goToDataHandling} style={{ cursor: 'pointer', color: '#006BB3', textDecoration: 'underline' }}>
                                    Policy Details
                                </span>
                            </h5>
                            <div className="row g-3 ps-2">
                                {/* Row 1 */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Policy Number <Req /></label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        placeholder="Enter Policy Number"
                                        value={form.policyNumber}
                                        onChange={(e) => handleChange('policyNumber', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Insured Name <Req /></label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        value={form.insuredName}
                                        readOnly
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Policy Status <Req /></label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        value={form.policyStatus}
                                        readOnly
                                        disabled={!isEditMode}
                                    />
                                </div>

                                {/* Row 2 */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Inception Date <Req /></label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm claims-input"
                                        value={form.inceptionDate}
                                        readOnly
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Expiration Date <Req /></label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm claims-input"
                                        value={form.expirationDate}
                                        readOnly
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="form-label claims-label">Line of Business</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        value={form.lineOfBusiness}
                                        readOnly
                                        disabled={!isEditMode}
                                    />
                                </div>

                                {/* Row 3 */}
                                <div className="col-12">
                                    <label className="form-label claims-label">Broker Name</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        placeholder="Enter Broker Name"
                                        value={form.broker}
                                        onChange={(e) => handleChange('broker', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Validation Status */}
                    <section className="card claims-card mb-2" style={{ borderRadius: '10px' }}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="card-title mb-3 fw-bold"
                                >
                                    <span onClick={goToValidation} style={{ cursor: 'pointer', color: '#006BB3', textDecoration: 'underline' }}>
                                        Validation Status
                                    </span>
                                </h5>
                                {loadingValidity ? (
                                    <div className="text-muted small">Loading validity data...</div>
                                ) : (
                                    <div className="d-flex align-items-center" style={{ width: '40%' }}>
                                        <div className="progress flex-grow-1 me-2" style={{ height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
                                            <div
                                                className="progress-bar"
                                                role="progressbar"
                                                style={{
                                                    width: `${validationProgress}%`,
                                                    backgroundColor: getProgressBarColor(validationProgress),
                                                    borderRadius: '4px'
                                                }}
                                                aria-valuenow={validationProgress}
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                            />
                                        </div>
                                        <span className="fw-bold">{validationProgress}%</span>
                                    </div>
                                )}
                            </div>

                            <div className="mb-3">
                                <span className="small fw-bold">Status: </span>
                                <span className="small fw-bold">{validationStatus}</span>
                            </div>

                            <div className="p-3 bg-light-subtle rounded border" style={{ backgroundColor: '#F8F9FA' }}>
                                <p className="mb-0 small text-dark">
                                    {validationSummary}
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Assignment Team */}
                    <section className="card claims-card mb-2" style={{ borderRadius: '10px' }}>
                        <div className="card-body">
                            <h5 className="card-title mb-3 fw-bold"
                            >
                                <span onClick={goToTriaging} style={{ cursor: 'pointer', color: '#006BB3', textDecoration: 'underline' }}>
                                    Assignment Team
                                </span>
                            </h5>

                            <div className='ps-2'>
                                <div className="mb-3">
                                    <label className="form-label claims-label">Region</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm claims-input"
                                        value={form.region}
                                        onChange={(e) => handleChange('region', e.target.value)}
                                        disabled={!isEditMode}
                                    />
                                    {assignmentData && assignmentData.handler_region &&
                                        assignmentData.handler_region !== assignmentData.adjuster_region && (
                                            <small className="text-muted">
                                                Handler Region: {assignmentData.handler_region}
                                            </small>
                                        )}
                                </div>

                                <div className="row">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label claims-label">Claim Adjuster <Req /></label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm claims-input"
                                            placeholder="Enter Claim Adjuster"
                                            value={form.adjuster}
                                            onChange={(e) => handleChange('adjuster', e.target.value)}
                                            disabled={!isEditMode}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label claims-label">Claim Handler <Req /></label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm claims-input"
                                            placeholder="Enter Claim Handler"
                                            value={form.handler}
                                            onChange={(e) => handleChange('handler', e.target.value)}
                                            disabled={!isEditMode}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Document Attached */}
                    <section className="card claims-card mb-2" style={{ borderRadius: '10px' }}>
                        <div className="card-body">
                            <h5 className="card-title mb-3 fw-bold"
                            >
                                <span onClick={goToDataHandling} style={{ cursor: 'pointer', color: '#006BB3', textDecoration: 'underline' }}>
                                    Document Attached
                                </span>
                            </h5>

                            {loadingDocuments ? (
                                <div className="text-center py-2">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : documents.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                    {documents.map((doc, index) => (
                                        <li key={doc.uniqueKey || index} className="mb-1">
                                            <span
                                                onClick={() => {
                                                    const url = doc.preview_url || doc.download_url;
                                                    if (url) {
                                                        window.open(url, '_blank', 'noopener,noreferrer');
                                                    }
                                                }}
                                                className="text-primary"
                                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            >
                                                {doc.name}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-muted small fst-italic">
                                    No documents attached
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Comments Section */}
                    {/* <section className="card mb-2" style={{ borderRadius: '10px' }}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                <label className="form-label fw-bold mb-0">
                                    Comments
                                </label>
                                <div className="d-flex gap-1 align-items-center flex-wrap">
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Bold" onClick={handleBold} style={{ width: '1.75rem', height: '1.75rem' }}>
                                        <strong style={{ fontSize: '0.85rem' }}>B</strong>
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Italic" onClick={handleItalic} style={{ width: '1.75rem', height: '1.75rem' }}>
                                        <em style={{ fontSize: '0.85rem' }}>I</em>
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Underline" onClick={handleUnderline} style={{ width: '1.75rem', height: '1.75rem' }}>
                                        <u style={{ fontSize: '0.85rem' }}>U</u>
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Strikethrough" onClick={handleStrikethrough} style={{ width: '1.75rem', height: '1.75rem' }}>
                                        <s style={{ fontSize: '0.85rem' }}>S</s>
                                    </button>
                                    <div className="vr" style={{ height: '1.25rem' }}></div>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Bullet List" onClick={handleBulletList} style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        ≡
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Numbered List" onClick={handleNumberedList} style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        ≣
                                    </button>
                                    <div className="vr" style={{ height: '1.25rem' }}></div>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Link" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        🔗
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Image" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        🖼
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="More" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        ⋯
                                    </button>
                                </div>
                            </div>

                            <textarea
                                ref={commentTextareaRef}
                                className="form-control"
                                rows="6"
                                placeholder=""
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                style={{
                                    resize: 'none',
                                    borderRadius: '6px',
                                    borderColor: '#ced4da'
                                }}
                            ></textarea>
                        </div>
                    </section> */}

                    {/* Action Buttons */}
                    <section className="card claims-card mb-0" style={{ borderRadius: '10px' }}>
                        <div className="card-body">
                            {claimError && (
                                <div className="alert alert-danger py-2 mb-2 small" role="alert">
                                    {claimError}
                                </div>
                            )}
                            <div className="d-flex justify-content-between gap-2 action-buttons-container">
                                <button
                                    type="button"
                                    className="btn claims-action-btn claims-btn-outline"
                                    onClick={onPrevious}
                                >
                                    Previous
                                </button>
                                <div className="d-flex gap-2 action-buttons-container">
                                    {!isEditMode ? (
                                        <>
                                            {/* Unified Complete & Generate Claim ID button */}
                                            <button
                                                type="button"
                                                className={`btn claims-action-btn ${hasClaimId
                                                    ? 'btn-success'
                                                    : (!allPriorStepsCompleted ? '' : 'claims-btn-primary')
                                                    }`}
                                                style={{
                                                    backgroundColor: hasClaimId
                                                        ? '#006BB3'
                                                        : (!allPriorStepsCompleted ? '#CCCCCC' : '#006BB3'),
                                                    borderColor: hasClaimId
                                                        ? '#006BB3'
                                                        : (!allPriorStepsCompleted ? '#CCCCCC' : '#006BB3'),
                                                    color: 'white',
                                                    cursor: hasClaimId || !allPriorStepsCompleted ? 'not-allowed' : 'pointer'
                                                }}
                                                onClick={handleCompleteAndGenerateClaim}
                                                disabled={hasClaimId || !allPriorStepsCompleted || loadingClaim}
                                            >
                                                {loadingClaim ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Processing...
                                                    </>
                                                ) : hasClaimId ? (
                                                    <>
                                                        <span style={{ marginRight: '6px' }}>✓</span>
                                                        Claim ID Generated
                                                    </>
                                                ) : (
                                                    'Complete & Generate Claim ID'
                                                )}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className="btn claims-action-btn"
                                                style={{
                                                    backgroundColor: 'white',
                                                    borderColor: '#006BB3',
                                                    color: '#006BB3'
                                                }}
                                                onClick={handleCancel}
                                            >
                                                <i className="bi bi-x-circle me-1"></i>
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn claims-action-btn claims-btn-primary"
                                                onClick={handleSave}
                                            >
                                                <i className="bi bi-check-circle me-1"></i>
                                                Save Changes
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Confirmation Modal */}
            <ClaimConfirmationModal
                show={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={executeCompleteAndGenerateClaim}
            />
        </div>
    );
};

export default ClaimsSetUpPage;
